import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import {
  Button,
  LeftCard,
  RightCard,
  TextSeparator,
} from '@sandtech-hub-monorepo/ui-kit';
import {
  SocialProviderType,
  useCognito,
} from '@sandtech-hub-monorepo/ui-auth-kit';

import { SignInForm } from './components/SignInForm';
import {
  signInTitle,
  signInSubtitle,
  signInCTAQuestion,
  signInCTAText,
} from '../../../../stringsManager';
import React from 'react';

export interface SignInProps {
  resetPasswordRoute: string;
  signUpRoute?: string;
  confirmSignUpRoute: string;
  homeRoute: string;
  updatePasswordRoute: string;
  supportRoute?: string;
  loginCover: string;
  signInWithGoogleLogo?: React.ReactNode;
  signInWithFacebookLogo?: React.ReactNode;
  signInWithLinkedInLogo?: React.ReactNode;
  signInWithEhubLogo?: React.ReactNode;
  /**
   * Fired after a successful email+password sign in (nextStep === 'DONE').
   * Not called for MFA / new-password-required / unconfirmed flows.
   * Social sign in success is reported via CognitoProvider's onSocialSignIn.
   */
  onSignInSuccess?: () => void;
}

export const SignIn = (props: SignInProps) => {
  const { signIn, socialSignIn, currentUser, loading } = useCognito();

  const navigate = useNavigate();
  const [params] = useSearchParams();

  if (currentUser) {
    navigate({
      pathname: props.homeRoute,
    });
  }

  const handleUsernamePasswordSignIn = async (values: {
    email: string;
    password: string;
  }) => {
    const result = await signIn({
      username: values.email,
      password: values.password,
      options: {
        authFlowType: 'USER_PASSWORD_AUTH',
      },
    });

    if (result) {
      switch (result.nextStep.signInStep) {
        case 'CONFIRM_SIGN_UP':
          navigate({
            pathname: props.confirmSignUpRoute,
            search: `email=${encodeURIComponent(values.email)}`,
          });
          toast(
            'Your account has not yet been activated. Please check your inbox (and spam folder) for the confirmation email.'
          );
          break;
        case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
          navigate({
            pathname: props.updatePasswordRoute,
          });
          break;
        case 'DONE':
          props.onSignInSuccess?.();
          break;
        default:
      }
    }
  };

  const handleGoogleSignIn = () => {
    socialSignIn('Google');
  };

  const handleFacebookSignIn = () => {
    socialSignIn('Facebook');
  };

  const handleLinkedInSignIn = () => {
    socialSignIn({ custom: 'LinkedIn' } as SocialProviderType);
  };

  const handleEhubSignIn = () => {
    socialSignIn({ custom: 'Ehub' } as SocialProviderType);
  };

  return (
    <>
      <LeftCard backgroundImage={props.loginCover} />
      <RightCard>
        {loading ? (
          <div className="flex items-center justify-center gap-2">
            <Loader2 className="animate-spin" />
            Loading ...
          </div>
        ) : (
          <>
            <div className="flex flex-col space-y-2">
              <h1 className="text-2xl font-semibold tracking-tight">
                {signInTitle}
              </h1>
              <p className="text-sm text-muted-foreground">{signInSubtitle}</p>
            </div>
            {[
              props.signInWithGoogleLogo,
              props.signInWithLinkedInLogo,
              props.signInWithFacebookLogo,
              props.signInWithEhubLogo,
            ].some((x) => !!x) && (
              <TextSeparator
                text="Social sign in options"
                className="font-semibold"
              />
            )}
            <div className="flex gap-4">
              {props.signInWithGoogleLogo ? (
                <Button
                  onClick={handleGoogleSignIn}
                  className="w-full bg-background text-gray-700 border active:bg-primary hover:bg-card sm:hover:bg-background-tertiary"
                  disabled={loading}
                >
                  {props.signInWithGoogleLogo}
                </Button>
              ) : null}
              {props.signInWithLinkedInLogo ? (
                <Button
                  onClick={handleLinkedInSignIn}
                  className="w-full bg-background text-gray-700 border active:bg-primary hover:bg-card sm:hover:bg-background-tertiary"
                  disabled={loading}
                >
                  {props.signInWithLinkedInLogo}
                </Button>
              ) : null}
              {props.signInWithFacebookLogo ? (
                <Button
                  onClick={handleFacebookSignIn}
                  className="w-full bg-background text-gray-700 border active:bg-primary hover:bg-card sm:hover:bg-background-tertiary"
                  disabled={loading}
                >
                  {props.signInWithFacebookLogo}
                </Button>
              ) : null}
              {props.signInWithEhubLogo && (
                <Button
                  onClick={handleEhubSignIn}
                  className="w-full border-[rgb(2,157,224)] bg-transparent text-[rgb(2,157,224)] hover:text-white border active:text-white active:bg-[rgb(5,140,224)] hover:bg-[rgb(2,157,224)]"
                  disabled={loading}
                >
                  {props.signInWithEhubLogo}
                </Button>
              )}
            </div>
            {[
              props.signInWithGoogleLogo,
              props.signInWithLinkedInLogo,
              props.signInWithFacebookLogo,
              props.signInWithEhubLogo,
            ].some((x) => !!x) && (
              <TextSeparator text="Manual sign in" className="font-semibold" />
            )}
            <SignInForm
              onSubmit={handleUsernamePasswordSignIn}
              loading={loading}
              resetPasswordRoute={props.resetPasswordRoute}
              defaultValues={{
                email: decodeURIComponent(params.get('email') || ''),
              }}
              searchParams={params}
            />
            {props.signUpRoute ? (
              <div className="flex gap-1 leading-tight justify-center items-center">
                <p className="text-sm text-muted-foreground">
                  {signInCTAQuestion}
                </p>
                <Link
                  to={props.signUpRoute}
                  className="text-sm text-primary leading-tight"
                >
                  {signInCTAText}
                </Link>
              </div>
            ) : null}

            {props.supportRoute ? (
              <div className="flex flex-col items-center text-sm">
                <p className="text-card-foreground font-medium">
                  Struggling to log in or sign up?
                </p>
                <p className="text-muted-foreground">
                  <Link
                    to={props.supportRoute}
                    className="text-sm text-primary leading-tight"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Click here
                  </Link>{' '}
                  to contact us
                </p>
              </div>
            ) : null}
          </>
        )}
      </RightCard>
    </>
  );
};
